import { animate, query, stagger, style, transition, trigger } from '@angular/animations'
import { NgClass } from '@angular/common'
import { Component, DestroyRef, OnInit, computed, inject, input, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { distinctUntilChanged, map } from 'rxjs'
import { FORM_FIELD_LIBRARY } from '../data/form-field-library.data'
import { FormFieldLibraryItem } from '../data/form-field-library.types'

@Component({
  selector: 'app-fields-library',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule],
  templateUrl: './fields-library.component.html',
  host: {
    class: 'tw-block tw-h-full tw-min-h-0 tw-shrink-0'
  },
  animations: [
    trigger('viewSwap', [
      transition(':enter', [style({ opacity: 0, transform: 'translateY(8px)' }), animate('220ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))]),
      transition(':leave', [animate('160ms ease-in', style({ opacity: 0, transform: 'translateY(-6px)' }))])
    ]),
    trigger('listStagger', [
      transition(':enter', [
        query('li', [style({ opacity: 0, transform: 'translateY(6px)' }), stagger(28, [animate('180ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))])], {
          optional: true
        })
      ]),
      transition('* => *', [
        query(':enter', [style({ opacity: 0, transform: 'translateY(6px)' }), stagger(28, [animate('180ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))])], {
          optional: true
        }),
        query(':leave', [stagger(16, [animate('120ms ease-in', style({ opacity: 0, transform: 'translateY(-4px)' }))])], { optional: true })
      ])
    ])
  ]
})
export class FieldsLibraryComponent implements OnInit {
  public readonly frequentlyUsed = input<FormFieldLibraryItem[]>([])
  public readonly loading = input(false)
  public readonly mobilePicker = input(false)
  public readonly fieldSelect = output<FormFieldLibraryItem>()

  public readonly searchControl = new FormControl('', { nonNullable: true })
  public readonly searchQuery = signal('')
  public readonly frequentlyUsedExpanded = signal(true)
  public readonly allFieldsExpanded = signal(true)
  public readonly comingSoonExpanded = signal(true)

  private readonly _destroyRef = inject(DestroyRef)

  /** Flat alphabetical list of available fields. */
  public readonly allFields = computed(() => this._sortFields(FORM_FIELD_LIBRARY.filter((field) => !field.coming_soon)))
  /** Flat alphabetical list of fields not available yet. */
  public readonly comingSoonFields = computed(() => this._sortFields(FORM_FIELD_LIBRARY.filter((field) => field.coming_soon)))

  public readonly isSearching = computed(() => this.searchQuery().trim().length > 0)

  public readonly searchResults = computed(() => this.searchFormFields(this.searchQuery()))

  /** Stable key so list stagger re-runs when search result set changes. */
  public readonly searchListKey = computed(() =>
    this.searchResults()
      .map((f) => f.form_element_type_id)
      .join(',')
  )

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        map((value) => value ?? ''),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((value) => this.searchQuery.set(value))
  }

  public clearSearch(): void {
    this.searchControl.setValue('')
    this.searchQuery.set('')
  }

  public toggleFrequentlyUsed(): void {
    this.frequentlyUsedExpanded.update((v) => !v)
  }

  public toggleAllFields(): void {
    this.allFieldsExpanded.update((v) => !v)
  }

  public toggleComingSoon(): void {
    this.comingSoonExpanded.update((v) => !v)
  }

  /** Class for form library icon set. */
  public iconClass(field: FormFieldLibraryItem): string {
    return this.formFieldIconClass(field.icon)
  }

  public onFieldClick(field: FormFieldLibraryItem): void {
    if (field.coming_soon) return
    this.fieldSelect.emit(field)
  }

  public formFieldIconClass(iconKey: string): string {
    return `fl-icon fl-icon-${iconKey || 'translate'}`
  }

  /**
   * Flat unique field list whose English or Arabic name contains the query. Searches the full catalogue.
   * @param query - The search query
   * @returns The matching fields
   **/
  private searchFormFields(query: string): FormFieldLibraryItem[] {
    const normalizedQuery = query.trim().toLocaleLowerCase()
    if (!normalizedQuery) return []

    const seenFieldTypeIds = new Set<number>()
    const matchingFields: FormFieldLibraryItem[] = []

    for (const field of FORM_FIELD_LIBRARY) {
      if (seenFieldTypeIds.has(field.form_element_type_id)) continue

      const englishName = field.name.toLocaleLowerCase()
      const arabicName = field.name_ar.toLocaleLowerCase()
      const nameMatchesQuery = englishName.includes(normalizedQuery) || arabicName.includes(normalizedQuery)
      if (!nameMatchesQuery) continue

      seenFieldTypeIds.add(field.form_element_type_id)
      matchingFields.push(field)
    }

    return matchingFields.sort((a, b) => {
      if (a.coming_soon !== b.coming_soon) return Number(a.coming_soon) - Number(b.coming_soon)
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    })
  }

  private _sortFields(fields: FormFieldLibraryItem[]): FormFieldLibraryItem[] {
    return [...fields].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  }
}
